import React from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  EnvelopeIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';

import * as API from '../../api';

/**
 * Password reset — premium redesign (was Material-UI + the legacy banner/card).
 *
 * Two modes on the same route, exactly as before:
 *   /reset          → ask for the email, send the reset link  (sendForgot)
 *   /reset/:key     → set a new password using the token      (sendResetPassword)
 *
 * All logic is preserved verbatim: same API calls, same field names
 * (email / password / password_confirmation + the appended `token`), same
 * 422/404 error mapping, same success messages and post-reset redirect.
 */
export default function Reset() {
  const { key = null } = useParams();
  const history = useHistory();
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState(false);
  const [passwordReset, setPasswordReset] = React.useState(false);
  const [emailHelper, setEmailHelper] = React.useState('');
  const [passwordHelper, setPasswordHelper] = React.useState('');
  const [repasswordHelper, setRepasswordHelper] = React.useState('');
  const [message, setMessage] = React.useState('');

  const sendResetPassword = (event) => {
    event.preventDefault();
    setLoading(true);
    setPasswordHelper('');
    setRepasswordHelper('');
    setMessage('');

    const formData = new FormData(event.target);
    formData.append('token', key);

    API.ResetPassword(formData)
      .then(() => {
        setMessage('Félicitations, votre mot de passe a été modifié avec succès');
        event.target.reset();
        setPasswordReset(true);
        setTimeout(() => {
          // The old code sent users to /connection?target=login, a route that
          // no longer resolves (it needs a :provider). Sign-in is a modal on
          // the home page now, so land there instead.
          history.push('/');
        }, 2000);
      })
      .catch((err) => {
        if (err.status === 422) {
          if (err.data.errors.password) setPasswordHelper(err.data.errors.password);
          if (err.data.errors.password_confirmation) {
            setRepasswordHelper(err.data.errors.password_confirmation);
          }
        }
      })
      .then(() => setLoading(false));
  };

  const sendForgot = (event) => {
    event.preventDefault();
    setLoading(true);
    setEmailHelper('');
    setMessage('');

    const formData = new FormData(event.target);

    API.ForgotPassword(formData)
      .then(() => {
        setMessage(
          'Merci, dans quelques instants vous allez recevoir un message dans votre boîte mail qui contient un lien de renitialisation du mot de passe.'
        );
        event.target.reset();
      })
      .catch((err) => {
        if (err.status === 404) setEmailHelper(err.data.errors);
      })
      .then(() => setLoading(false));
  };

  return (
    <div className="relative min-h-[70vh] overflow-hidden bg-surface-muted">
      {/* Brand-tinted backdrop, matching the partner/admin auth pages */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand-50 via-surface-muted to-surface-muted" />
      <div aria-hidden className="pointer-events-none absolute -top-32 -start-24 h-96 w-96 rounded-full bg-brand-500/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -end-24 h-96 w-96 rounded-full bg-accent-500/20 blur-3xl" />

      <div className="relative mx-auto flex w-full max-w-container flex-col items-center px-4 py-12 sm:px-6 sm:py-16">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="rounded-3xl border border-white/70 bg-white/90 p-6 shadow-elevated backdrop-blur-sm sm:p-8">
            <div className="text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                {key ? <LockClosedIcon className="h-6 w-6" /> : <KeyIcon className="h-6 w-6" />}
              </span>
              <h1 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-ink">
                {key ? 'Nouveau mot de passe' : 'Mot de passe oublié'}
              </h1>
              <p className="mt-1.5 text-sm text-ink-muted">
                {key
                  ? 'Choisissez un nouveau mot de passe pour votre compte.'
                  : 'Saisissez votre adresse e-mail et nous vous enverrons un lien de réinitialisation.'}
              </p>
            </div>

            {message ? (
              <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{message}</span>
              </div>
            ) : null}

            {key ? (
              /* ---- Set a new password ---- */
              <form onSubmit={sendResetPassword} method="POST" className="mt-6 space-y-4">
                <Field
                  name="password"
                  type="password"
                  label="Nouveau mot de passe"
                  placeholder="••••••••"
                  icon={LockClosedIcon}
                  error={passwordHelper}
                  disabled={loading || passwordReset}
                  autoComplete="new-password"
                />
                <Field
                  name="password_confirmation"
                  type="password"
                  label="Confirmer le mot de passe"
                  placeholder="••••••••"
                  icon={LockClosedIcon}
                  error={repasswordHelper}
                  disabled={loading || passwordReset}
                  autoComplete="new-password"
                />
                <SubmitButton loading={loading} disabled={loading || passwordReset} label="Changer le mot de passe" />
              </form>
            ) : (
              /* ---- Request a reset link ---- */
              <form onSubmit={sendForgot} method="POST" className="mt-6 space-y-4">
                <Field
                  name="email"
                  type="email"
                  label="Adresse e-mail"
                  placeholder="vous@exemple.com"
                  icon={EnvelopeIcon}
                  error={emailHelper}
                  disabled={loading}
                  autoComplete="email"
                />
                <SubmitButton loading={loading} disabled={loading} label="Envoyer le lien" />
              </form>
            )}

            <div className="mt-6 border-t border-slate-100 pt-5 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500 transition-colors hover:text-brand-600"
              >
                <ArrowLeftIcon className="h-4 w-4 rtl:-scale-x-100" />
                {t('nav.backHome', "Retour à l'accueil")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- presentational helpers ---- */

function Field({ name, type = 'text', label, placeholder, icon: Icon, error, disabled, autoComplete }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-subtle">
        {label}
      </label>
      <div
        className={`flex h-12 items-center gap-2.5 rounded-full border bg-white ps-4 pe-4 transition ${
          error
            ? 'border-rose-300 focus-within:border-rose-400 focus-within:ring-2 focus-within:ring-rose-400/15'
            : 'border-slate-200 hover:border-slate-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/15'
        }`}
      >
        <Icon className="h-4 w-4 shrink-0 text-ink-subtle" />
        <input
          name={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className="min-w-0 flex-1 bg-transparent text-sm font-medium text-ink placeholder:text-ink-subtle outline-none disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>
      {error ? (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-rose-600">
          <ExclamationCircleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}

function SubmitButton({ loading, disabled, label }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-6 font-semibold text-white shadow-card transition-colors hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? (
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
      ) : (
        label
      )}
    </button>
  );
}
